/**
 * Quote API Service for Netlify Functions
 * 
 * Replaces direct Firebase Admin SDK calls with HTTP API calls to Netlify Functions.
 * Uses "quotes" collection and human-readable IDs (YYYYMMDD-BUNO-A7X).
 */

// API endpoint configuration
const API_BASE = '/.netlify/functions';

// HTTP client with error handling
class ApiClient {
  private async request(endpoint: string, options: RequestInit = {}): Promise<unknown> {
    const url = `${API_BASE}${endpoint}`;
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async post(endpoint: string, data: unknown): Promise<unknown> {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async get(endpoint: string): Promise<unknown> {
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async put(endpoint: string, data: unknown): Promise<unknown> {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch(endpoint: string, data: unknown): Promise<unknown> {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }
}

const apiClient = new ApiClient();

// Price snapshot interface for capturing pricing data at quote creation time
export interface PriceSnapshot {
  bundlePrice: number;
  bunoPrice: number;
  totalPrice: number;
  capturedAt: string; // ISO timestamp
  shopifyVariantIds: string[];
}

// Quote version types (numeric only)
export type QuoteVersion = 1 | 2; // Numeric versions only

// Internal version storage types (supports legacy and new versioning)
export type StoredQuoteVersion = 1 | 2 | 'v1' | 'v2' | '1.0.0' | number;

// Bundle info interface for v2 quotes
export interface BundleInfo {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  isShopifyBundle: boolean;
  components: Array<{
    name: string;
    type: string;
  }>;
  bundleMapping?: {
    shopifyHandle: string;
    variants: {
      S: string;
      M: string;
      L: string;
    };
  };
}

// V1 user data structure (legacy individual product selection)
export interface V1UserData {
  maskSizing: unknown | null;
  equipoSeleccionado: unknown | null;
  mascarillaSeleccionada: unknown | null;
  bunoIncluido: boolean;
  bundleInfo?: unknown; // Optional for transition period
}

// V2 user data structure (new kit-based selection)
export interface V2UserData {
  bunoIncluido: boolean;
  bundleInfo: BundleInfo;
  maskSizing?: {
    maskSize?: string;
  } | null;
  // Individual products are derived from bundleInfo, not stored separately
}

// Type definitions for API responses
export interface ApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  data?: T;
}

export interface QuoteResponse extends ApiResponse {
  id: string;
}

export interface GetQuoteResponse extends ApiResponse {
  quote: UserQuoteApi;
}

// API-compatible user quote interface (with serialized timestamps)
export interface UserQuoteApi {
  id: string;
  timestamp: string; // ISO string instead of Firestore Timestamp
  createdAt?: string; // ISO string for expiration logic
  sessionId: string;
  userData: V1UserData | V2UserData;
  metadata: {
    userAgent?: string;
    ipAddress?: string;
    stage: 'productos' | 'plan-seleccion' | 'carrito';
    completedSteps: string[];
    breathingSelfReport?: string;
    noseScore?: number;
    maskRecommendation?: string;
  };
  priceSnapshot?: PriceSnapshot; // Immutable pricing data captured at creation time
  expiresAt?: string; // ISO string instead of Firestore Timestamp (optional - no longer used)
  status: 'active' | 'expired' | 'used';
  version: StoredQuoteVersion; // Supports legacy strings and new numeric versions
  environment: string;
  updatedAt?: string; // ISO string
  usedAt?: string; // ISO string
}

// Quote creation payload (version-aware)
export interface CreateQuotePayload {
  sessionId: string;
  userData: V1UserData | V2UserData;
  metadata: UserQuoteApi['metadata'];
  priceSnapshot: PriceSnapshot; // Immutable pricing data captured at creation time
  expiresAt?: string; // ISO string (optional - no longer used)
  status: 'active';
  version?: StoredQuoteVersion; // Optional, uses global config or explicit version
}

// API functions that mirror the original Firebase functions

/**
 * Create user quote via Netlify Function
 * Returns human-readable quote ID (YYYYMMDD-BUNO-A7X)
 */
export async function createUserQuote(quoteData: CreateQuotePayload): Promise<string> {
  try {
    const response = await apiClient.post('/create-quote', quoteData) as QuoteResponse;
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to create quote');
    }
    
    console.log(`📝 Quote created via API: ${response.id}`);
    return response.id;
    
  } catch (error) {
    console.error('API - Failed to create quote:', error);
    throw new Error(`Failed to create quote: ${error}`);
  }
}

/**
 * Get user quote by human-readable ID via Netlify Function
 */
export async function getUserQuote(quoteId: string): Promise<UserQuoteApi | null> {
  try {
    const response = await apiClient.get(`/get-quote/${quoteId}`) as GetQuoteResponse;
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to get quote');
    }
    
    console.log(`📖 Quote retrieved via API: ${quoteId}`);
    return response.quote;
    
  } catch (error) {
    // Handle 404 and 410 (expired) as null return, not errors
    if (error instanceof Error) {
      if (error.message.includes('404') || error.message.includes('not found')) {
        console.log(`📖 Quote not found: ${quoteId}`);
        return null;
      }
      // Quotes no longer expire, so no need to handle expiration
    }
    
    console.error('API - Failed to get quote:', error);
    throw new Error(`Failed to get quote: ${error}`);
  }
}

/**
 * Update user quote via Netlify Function
 */
export async function updateUserQuote(
  quoteId: string,
  updates: Partial<UserQuoteApi>
): Promise<void> {
  try {
    const response = await apiClient.put(`/update-quote/${quoteId}`, updates) as ApiResponse;
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to update quote');
    }
    
    console.log(`📝 Quote updated via API: ${quoteId}`);
    
  } catch (error) {
    console.error('API - Failed to update quote:', error);
    throw new Error(`Failed to update quote: ${error}`);
  }
}

/**
 * Mark quote as used via Netlify Function
 */
export async function markQuoteAsUsed(quoteId: string): Promise<void> {
  try {
    const response = await apiClient.post('/mark-quote-used', { id: quoteId }) as ApiResponse;
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to mark quote as used');
    }
    
    console.log(`✅ Quote marked as used via API: ${quoteId}`);
    
  } catch (error) {
    console.error('API - Failed to mark quote as used:', error);
    throw new Error(`Failed to mark quote as used: ${error}`);
  }
}

/**
 * Cleanup expired quotes via Netlify Function
 */
export async function cleanupExpiredQuotes(): Promise<number> {
  try {
    const response = await apiClient.post('/cleanup-quotes', {}) as ApiResponse & { cleanedUp: number };
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to cleanup expired quotes');
    }
    
    const count = response.cleanedUp || 0;
    console.log(`🧹 Cleaned up ${count} expired quotes via API`);
    return count;
    
  } catch (error) {
    console.error('API - Failed to cleanup expired quotes:', error);
    throw new Error(`Failed to cleanup expired quotes: ${error}`);
  }
}

/**
 * Create timestamp for API (ISO string)
 */
export function createTimestamp(date?: Date): string {
  return (date || new Date()).toISOString();
}

/**
 * Convert API timestamp string to Date
 */
export function timestampToDate(timestamp: string): Date {
  return new Date(timestamp);
}

/**
 * Health check for API functions
 */
export async function checkApiHealth(): Promise<boolean> {
  try {
    // Try to cleanup (which is a safe read-only-ish operation)
    await cleanupExpiredQuotes();
    return true;
  } catch (error) {
    console.error('API health check failed:', error);
    return false;
  }
}

// Quote ID validation (client-side)
export function isValidQuoteId(quoteId: string): boolean {
  const quoteIdRegex = /^(\d{8})-([A-Z0-9]+)-([A-Z0-9]{3})$/;
  return quoteIdRegex.test(quoteId);
}

// Parse quote ID components (client-side)
export function parseQuoteId(quoteId: string) {
  const quoteIdRegex = /^(\d{8})-([A-Z0-9]+)-([A-Z0-9]{3})$/;
  const match = quoteId.match(quoteIdRegex);
  
  if (!match) {
    return { isValid: false, error: 'Invalid quote ID format' };
  }
  
  const [, date, doctor, random] = match;
  
  return {
    isValid: true,
    date,
    doctor,
    random,
    fullId: quoteId,
    parsedDate: {
      year: parseInt(date.substring(0, 4)),
      month: parseInt(date.substring(4, 6)),
      day: parseInt(date.substring(6, 8))
    }
  };
}

// Export types for use in other files
export type { UserQuoteApi as UserQuote, CreateQuotePayload, StoredQuoteVersion };